import { SecondaryButton } from "components/ui/secondary-button";
import * as React from "react";
import { Content, PageHeader, Stage } from "../layout";

const ASYNC_FAILURE_DELAY_MS = 1500;

const delay = (ms: number): Promise<void> =>
    new Promise(resolve => {
        setTimeout(resolve, ms);
    });

class DebugSyncError extends Error {
    readonly name = "DebugSyncError";
}

class DebugAsyncError extends Error {
    readonly name = "DebugAsyncError";
}

const throwSynchronously = (): never => {
    throw new DebugSyncError(
        `Debug page synchronous throw at ${new Date().toISOString()}`,
    );
};

const failAfterDelay = async (): Promise<never> => {
    await delay(ASYNC_FAILURE_DELAY_MS);
    throw new DebugAsyncError(
        `Debug page async rejection after ${ASYNC_FAILURE_DELAY_MS}ms at ${new Date().toISOString()}`,
    );
};

export const Debug: React.FunctionComponent = () => {
    const [asyncPending, setAsyncPending] = React.useState(false);

    const startAsyncFailure = () => {
        setAsyncPending(true);
        failAfterDelay().finally(() => setAsyncPending(false));
    };

    return (
        <Stage>
            <PageHeader title="Debug" />
            <Content>
                <div className="space-y-4">
                    <div className="space-y-1">
                        <div>
                            <SecondaryButton type="button" onClick={throwSynchronously}>
                                Throw synchronous exception
                            </SecondaryButton>
                        </div>
                        <p className="text-sm text-gray-500">
                            Throws from the click handler; reaches window.onerror.
                        </p>
                    </div>
                    <div className="space-y-1">
                        <div>
                            <SecondaryButton
                                type="button"
                                disabled={asyncPending}
                                onClick={startAsyncFailure}>
                                {asyncPending
                                    ? "Rejecting shortly…"
                                    : "Reject after async delay"}
                            </SecondaryButton>
                        </div>
                        <p className="text-sm text-gray-500">
                            Awaits {ASYNC_FAILURE_DELAY_MS}ms then rejects; reaches
                            window.onunhandledrejection.
                        </p>
                    </div>
                </div>
            </Content>
        </Stage>
    );
};
